import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import {
  getSocialMetricsForDate,
  saveSocialMetrics,
} from '@coparent/shared/src/services/adminMetricsService';

function todayKey() {
  return new Date().toISOString().slice(0, 10);
}

const FIELDS = [
  { key: 'followers', label: 'Followers' },
  { key: 'impressions', label: 'Impressions' },
  { key: 'profileVisits', label: 'Profile visits' },
  { key: 'postsPublished', label: 'Posts published' },
  { key: 'repliesGiven', label: 'Replies given' },
];

const EMPTY_PLATFORM = FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: '' }), {});

function toNumberMap(values) {
  const out = {};
  for (const { key } of FIELDS) {
    const v = values[key];
    out[key] = v === '' || v == null ? 0 : Number(v) || 0;
  }
  return out;
}

function fromDoc(platformDoc) {
  if (!platformDoc) return { ...EMPTY_PLATFORM };
  const out = {};
  for (const { key } of FIELDS) {
    out[key] = platformDoc[key] != null ? String(platformDoc[key]) : '';
  }
  return out;
}

const AdminSocialEntryPage = () => {
  const { currentUser } = useAuth();
  const [date, setDate] = useState(todayKey());
  const [threads, setThreads] = useState(EMPTY_PLATFORM);
  const [x, setX] = useState(EMPTY_PLATFORM);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      const existing = await getSocialMetricsForDate(date);
      if (cancelled) return;
      setThreads(fromDoc(existing?.threads));
      setX(fromDoc(existing?.x));
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [date]);

  const onChange = (setter) => (key) => (e) => {
    const v = e.target.value;
    // Allow empty string while typing; coerce only on save.
    if (v === '' || /^\d+$/.test(v)) setter((prev) => ({ ...prev, [key]: v }));
  };

  const onSave = async () => {
    if (!currentUser?.uid) {
      toast.error('Must be signed in');
      return;
    }
    setSaving(true);
    try {
      await saveSocialMetrics(date, currentUser.uid, {
        threads: toNumberMap(threads),
        x: toNumberMap(x),
      });
      toast.success(`Saved metrics for ${date}`);
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Failed to save');
    }
    setSaving(false);
  };

  return (
    <div className="animate-fade-in">
      <div className="page-header">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="page-title">Social metrics entry</h1>
            <p className="page-subtitle">
              Manual daily entry for Threads + X. Pulled into the admin dashboard.
            </p>
          </div>
          <Link
            to="/admin/metrics"
            className="text-sm text-canopy hover:text-accent-700 font-semibold"
          >
            ← Back to dashboard
          </Link>
        </div>
      </div>

      <div className="card p-6 mb-6 max-w-3xl">
        <div className="flex items-center gap-3 mb-6">
          <label className="text-xs font-semibold uppercase tracking-wide text-ink-500">
            Date
          </label>
          <input
            type="date"
            value={date}
            max={todayKey()}
            onChange={(e) => setDate(e.target.value)}
            className="input-base !w-auto"
          />
          {loading && <span className="text-xs text-ink-400">loading…</span>}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Threads column */}
          <div>
            <h3 className="font-bold text-ink-900 text-sm mb-3 flex items-center gap-2">
              <span className="inline-block w-2 h-2 rounded-full bg-canopy" />
              Threads
            </h3>
            <div className="space-y-3">
              {FIELDS.map((f) => (
                <div key={f.key}>
                  <label className="block text-xs font-semibold text-ink-600 mb-1.5 uppercase tracking-wide">
                    {f.label}
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={threads[f.key]}
                    onChange={onChange(setThreads)(f.key)}
                    className="input-base"
                    placeholder="0"
                    disabled={loading}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* X column */}
          <div>
            <h3 className="font-bold text-ink-900 text-sm mb-3 flex items-center gap-2">
              <span className="inline-block w-2 h-2 rounded-full bg-ink-800" />
              X
            </h3>
            <div className="space-y-3">
              {FIELDS.map((f) => (
                <div key={f.key}>
                  <label className="block text-xs font-semibold text-ink-600 mb-1.5 uppercase tracking-wide">
                    {f.label}
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={x[f.key]}
                    onChange={onChange(setX)(f.key)}
                    className="input-base"
                    placeholder="0"
                    disabled={loading}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-end gap-3">
          <button
            onClick={onSave}
            disabled={saving || loading}
            className="btn-primary"
          >
            {saving ? 'Saving…' : `Save metrics for ${date}`}
          </button>
        </div>
      </div>

      <p className="text-xs text-ink-400 max-w-3xl">
        Pull these numbers from each platform's native analytics view (Threads
        Insights, X Premium analytics, or your account home page). Save once per
        day; back-dating supported via the date picker above.
      </p>
    </div>
  );
};

export default AdminSocialEntryPage;
